import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  deleteSchedule,
  getSummary,
  listScheduleRuns,
  listSchedules,
  runScheduleNow,
  toggleSchedule,
  updateSchedule,
} from '../../services/schedulerApi';
import {
  ConfirmModal,
  OverflowMenu,
  Skeleton,
  Toast,
} from '../components';
import {
  STATUS_LABELS,
  absoluteTime,
  buildSchedulePayload,
  cadenceParts,
  cadenceText,
  durationText,
  isAlwaysFull,
  isPushJobType,
  lastRunAddedUpdated,
  relativeTime,
  streamForJobType,
  STREAMS,
  targetChipLabel,
  targetKey,
} from '../format';

const POLL_MS = 10000;
const OPEN_STATUSES = new Set(['pending', 'running']);
const FILTERS = [
  { id: 'all', label: 'All jobs' },
  { id: 'fail', label: 'Failing' },
  { id: 'active', label: 'Active' },
  { id: 'paused', label: 'Paused' },
  { id: 'push', label: 'Writes to a store' },
];

const errText = (e, fallback) => e?.response?.data?.detail || e?.message || fallback;

export default function Overview() {
  const navigate = useNavigate();
  const [schedules, setSchedules] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [, forceTick] = useState(0); // re-render countdowns

  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [closed, setClosed] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [runsCache, setRunsCache] = useState({});

  const bulkRef = useRef(false);
  const loadRef = useRef(() => Promise.resolve());

  const showToast = useCallback((message, error = false) => {
    setToast({ message, error });
    window.setTimeout(() => setToast(null), 3200);
  }, []);

  const load = useCallback(async (silent = false) => {
    if (bulkRef.current) return; // bulk loops own the state until they finish
    try {
      const [sch, sum] = await Promise.all([listSchedules(), getSummary()]);
      setSchedules(sch);
      setSummary(sum);
      setLoadError(null);
    } catch (e) {
      if (!silent) setLoadError(errText(e, 'Could not load schedules'));
    }
  }, []);
  loadRef.current = load;

  useEffect(() => {
    loadRef.current();
    const poll = setInterval(() => loadRef.current(true), POLL_MS);
    const tick = setInterval(() => forceTick((n) => n + 1), 30000);
    return () => { clearInterval(poll); clearInterval(tick); };
  }, []);

  // ── Streams ──────────────────────────────────────────────────────────
  const streams = useMemo(() => {
    const buckets = new Map(); // stream id -> { stream, items }
    (schedules || []).forEach((s) => {
      const stream = streamForJobType(s.job_type);
      if (!buckets.has(stream.id)) buckets.set(stream.id, { stream, items: [] });
      buckets.get(stream.id).items.push(s);
    });
    const ordered = STREAMS.filter((st) => buckets.has(st.id)).map((st) => buckets.get(st.id));
    if (buckets.has('other')) ordered.push(buckets.get('other'));
    return ordered;
  }, [schedules]);

  const failingSchedules = useMemo(
    () => (schedules || []).filter((s) => s.enabled && s.last_status === 'failed'),
    [schedules],
  );
  const failingIds = useMemo(() => new Set(failingSchedules.map((s) => s.id)), [failingSchedules]);

  // Shared root cause: same normalised error across 2+ distinct schedules,
  // from per-run errors (not the schedule's single last_job_error pointer).
  // Only schedules failing right now count — a run that failed while paused
  // or since fixed must not raise the banner.
  const cause = useMemo(() => {
    const bySig = new Map();
    (summary?.recent_failures || []).forEach((f) => {
      if (!f.error || !failingIds.has(f.schedule_id)) return;
      const sig = f.error.toLowerCase().replace(/\d+/g, '').replace(/\s+/g, ' ').trim().slice(0, 80);
      if (!sig) return;
      if (!bySig.has(sig)) bySig.set(sig, new Map());
      bySig.get(sig).set(f.schedule_id, f);
    });
    let best = null;
    bySig.forEach((schedMap, sig) => {
      if (schedMap.size >= 2 && (!best || schedMap.size > best.size)) best = { sig, size: schedMap.size };
    });
    return best;
  }, [summary, failingIds]);

  const stats = useMemo(() => {
    if (!summary) return null;
    const { succeeded = 0, failed = 0, runs = 0 } = summary.last_24h || {};
    const done = succeeded + failed;
    return {
      active: summary.schedules?.active ?? 0,
      total: summary.schedules?.total ?? 0,
      okPct: done ? Math.round((succeeded / done) * 100) : null,
      runs,
      failures: failed,
    };
  }, [summary]);

  // The banner's failing set, so "Retry all" hits exactly the jobs it shows.
  const causeIds = useMemo(() => {
    if (!cause) return [];
    const ids = new Set();
    (summary?.recent_failures || []).forEach((f) => {
      if (!f.error || !failingIds.has(f.schedule_id)) return;
      const sig = f.error.toLowerCase().replace(/\d+/g, '').replace(/\s+/g, ' ').trim().slice(0, 80);
      if (sig === cause.sig) ids.add(f.schedule_id);
    });
    return [...ids];
  }, [cause, summary, failingIds]);

  const runningNow = useMemo(
    () => (schedules || []).filter((s) => OPEN_STATUSES.has(s.last_status)),
    [schedules],
  );

  // ── Row actions ──────────────────────────────────────────────────────
  const patchSchedule = useCallback((id, patch) => {
    setSchedules((prev) => (prev || []).map((s) => (s.id === id ? { ...s, ...patch } : s)));
  }, []);

  const handleToggle = async (schedule, enabled) => {
    patchSchedule(schedule.id, { enabled });
    try {
      const updated = await toggleSchedule(schedule.id, enabled);
      setSchedules((prev) => prev.map((s) => (s.id === schedule.id ? { ...s, ...updated } : s)));
    } catch (e) {
      patchSchedule(schedule.id, { enabled: !enabled });
      showToast(errText(e, 'Could not update schedule'), true);
    }
  };

  const handleRunNow = async (schedule) => {
    try {
      await runScheduleNow(schedule.id);
      showToast(`${schedule.name} queued`);
      load(true);
    } catch (e) {
      showToast(errText(e, 'Could not start run'), true);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleteBusy(true);
    try {
      await deleteSchedule(pendingDelete.id);
      setSchedules((prev) => prev.filter((s) => s.id !== pendingDelete.id));
      showToast(`${pendingDelete.name} deleted`);
      setPendingDelete(null);
    } catch (e) {
      showToast(errText(e, 'Delete failed'), true);
    } finally {
      setDeleteBusy(false);
    }
  };

  const handleSaveCadence = async (schedule, cad) => {
    const payload = buildSchedulePayload(schedule, cad);
    try {
      const updated = await updateSchedule(schedule.id, payload);
      setSchedules((prev) => prev.map((s) => (s.id === schedule.id ? { ...s, ...updated } : s)));
      showToast(`${schedule.name} cadence saved`);
      setExpandedId(null);
    } catch (e) {
      showToast(errText(e, 'Could not save cadence'), true);
    }
  };

  // ── Bulk actions ─────────────────────────────────────────────────────
  const runBulk = async (fn) => {
    bulkRef.current = true;
    setBulkBusy(true);
    try {
      await fn();
    } finally {
      bulkRef.current = false;
      setBulkBusy(false);
      load(true);
    }
  };

  const handlePauseAll = () => {
    const targets = (schedules || []).filter((s) => s.enabled);
    runBulk(async () => {
      let failed = 0;
      for (const s of targets) {
        patchSchedule(s.id, { enabled: false });
        try {
          await toggleSchedule(s.id, false);
        } catch {
          failed += 1;
        }
      }
      showToast(
        failed ? `Paused ${targets.length - failed} of ${targets.length} jobs` : 'Every job paused',
        failed > 0,
      );
    });
  };

  const handleResumeAll = () => {
    const targets = (schedules || []).filter((s) => !s.enabled);
    runBulk(async () => {
      let failed = 0;
      for (const s of targets) {
        patchSchedule(s.id, { enabled: true });
        try {
          await toggleSchedule(s.id, true);
        } catch {
          failed += 1;
        }
      }
      showToast(
        failed ? `Resumed ${targets.length - failed} of ${targets.length} jobs` : 'Every job back on its cadence',
        failed > 0,
      );
    });
  };

  const handleRetryCause = () => {
    const ids = new Set(causeIds);
    const targets = failingSchedules.filter((s) => ids.has(s.id));
    runBulk(async () => {
      let failed = 0;
      for (const s of targets) {
        try {
          await runScheduleNow(s.id);
        } catch {
          failed += 1;
        }
      }
      showToast(
        failed ? `Queued ${targets.length - failed} of ${targets.length} retries` : `Retry queued for ${targets.length} jobs`,
        failed > 0,
      );
    });
  };

  // ── Filtering ────────────────────────────────────────────────────────
  const q = query.trim().toLowerCase();
  const matchSchedule = useCallback((s) => {
    const stream = streamForJobType(s.job_type);
    if (filter === 'fail' && !failingIds.has(s.id)) return false;
    if (filter === 'active' && !s.enabled) return false;
    if (filter === 'paused' && s.enabled) return false;
    if (filter === 'push' && stream.kind !== 'push') return false;
    if (q) {
      const hay = `${s.name} ${stream.label} ${stream.from} ${stream.to}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  }, [filter, failingIds, q]);

  const allCount = (schedules || []).length;
  const chipCounts = {
    all: allCount,
    fail: failingSchedules.length,
    active: (schedules || []).filter((s) => s.enabled).length,
    paused: (schedules || []).filter((s) => !s.enabled).length,
    push: (schedules || []).filter((s) => streamForJobType(s.job_type).kind === 'push').length,
  };

  const visibleStreams = streams
    .map(({ stream, items }) => ({ stream, items: items.filter(matchSchedule), all: items }))
    .filter(({ items }) => items.length);
  const anyFiltering = filter !== 'all' || q.length > 0;

  const loading = schedules === null;

  // ── Render ───────────────────────────────────────────────────────────
  return (
    <div>
      <div className="oc-header">
        <div className="oc-header-text">
          <h1 className="oc-title">Job scheduler</h1>
          <p className="oc-blurb">
            Every recurring job, grouped by the data it moves. Pulls bring Epicor records into the
            middleware and PIM; pushes send finished records out to the AU and NZ storefronts.
          </p>
        </div>
        <div className="oc-header-action" style={{ display: 'flex', gap: 'var(--oc-space-2)' }}>
          <button
            type="button"
            className="oc-btn oc-btn-secondary"
            disabled={bulkBusy || loading}
            title={stats && stats.active > 0
              ? `Stops all ${stats.active} scheduled jobs. Runs already in flight finish.`
              : 'Puts every job back on its own cadence.'}
            onClick={stats && stats.active > 0 ? handlePauseAll : handleResumeAll}
          >
            {stats && stats.active > 0 ? 'Pause everything' : 'Resume everything'}
          </button>
          <button
            type="button"
            className="oc-btn oc-btn-primary"
            onClick={() => navigate('/jobs/schedules/new')}
          >
            New schedule
          </button>
        </div>
      </div>

      {loadError && (
        <div className="oc-error" role="alert">
          <strong>Could not load schedules.</strong> {String(loadError)}
        </div>
      )}

      {loading ? (
        <>
          <div className="oc-blueprint" style={{ padding: 0, marginBottom: 'var(--oc-space-4)' }}>
            <Skeleton height={96} />
          </div>
          <Skeleton height={360} />
        </>
      ) : (
        <>
          {/* ── Health strip ── */}
          <div className="oc-blueprint" style={{ padding: 0, marginBottom: 'var(--oc-space-4)' }}>
            <i className="oc-corner tl" aria-hidden="true" />
            <i className="oc-corner tr" aria-hidden="true" />
            <i className="oc-corner bl" aria-hidden="true" />
            <i className="oc-corner br" aria-hidden="true" />
            <div className="sched-oc-strip">
              <div className="sched-oc-panel">
                <div className="sched-oc-kicker">Running now</div>
                {runningNow.length ? (
                  <>
                    <div className="sched-oc-runname">{runningNow[0].name}</div>
                    <div className="sched-oc-runnote">
                      {streamForJobType(runningNow[0].job_type).label}
                      {runningNow[0].last_job_started_at
                        ? ` · started ${relativeTime(runningNow[0].last_job_started_at)}`
                        : ' · queued'}
                      {runningNow.length > 1 ? ` · +${runningNow.length - 1} more` : ''}
                    </div>
                    <div className="sched-oc-runbar"><div className="sched-oc-runbar-indet" /></div>
                  </>
                ) : (
                  <>
                    <div className="sched-oc-runname">Idle</div>
                    <div className="sched-oc-runnote">
                      {summary?.upcoming?.[0]
                        ? `Nothing is running. Next job starts ${relativeTime(summary.upcoming[0].next_run_at)}.`
                        : 'Nothing is running and no job is scheduled — every job is paused.'}
                    </div>
                  </>
                )}
              </div>

              <div className="sched-oc-panel">
                <div className="sched-oc-kicker">Up next</div>
                <div className="sched-oc-upnext">
                  {(summary?.upcoming || []).slice(0, 4).map((u, i) => (
                    <div className="sched-oc-upnext-row" key={u.id}>
                      <span
                        className="sched-oc-upnext-when"
                        style={i === 0 ? undefined : { color: 'color-mix(in srgb, var(--oc-text) 62%, transparent)' }}
                        title={absoluteTime(u.next_run_at)}
                      >
                        {(relativeTime(u.next_run_at) || '').replace(/^in /, '')}
                      </span>
                      <span
                        className="sched-oc-upnext-label"
                        style={i === 0 ? undefined : { color: 'color-mix(in srgb, var(--oc-text) 62%, transparent)' }}
                      >
                        {streamForJobType(u.job_type).label} · {u.name}
                      </span>
                    </div>
                  ))}
                  {!(summary?.upcoming || []).length && (
                    <div className="sched-oc-upnext-row">
                      <span className="sched-oc-upnext-when">—</span>
                      <span className="sched-oc-upnext-label sched-oc-muted">
                        Nothing scheduled — every job is paused
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className={`sched-oc-panel sched-oc-panel-health ${failingSchedules.length > 0 ? 'bad' : 'ok'}`}>
                <div className={`sched-oc-kicker ${failingSchedules.length > 0 ? 'bad' : 'ok'}`}>Last 24 hours</div>
                {stats ? (
                  <>
                    <div className="sched-oc-healthrow">
                      <span className="sched-oc-healthpct">{stats.okPct != null ? `${stats.okPct}%` : '—'}</span>
                      <span className="sched-oc-healthnote">of {stats.runs} runs succeeded</span>
                    </div>
                    <div className="sched-oc-healthsub">
                      {failingSchedules.length > 0
                        ? `${failingSchedules.length} ${failingSchedules.length === 1 ? 'schedule is' : 'schedules are'} failing right now`
                        : 'No schedule is failing right now'}
                    </div>
                    {failingSchedules.length > 0 && filter !== 'fail' && (
                      <button type="button" className="sched-oc-healthbtn" onClick={() => { setFilter('fail'); setClosed({}); }}>
                        Show failing only
                      </button>
                    )}
                  </>
                ) : (
                  <div className="sched-oc-healthsub">—</div>
                )}
              </div>
            </div>
          </div>

          {/* ── Shared root-cause banner ── */}
          {cause && (
            <div className="sched-oc-cause">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0M12 9v4M12 17h.01" /></svg>
              <div className="sched-oc-cause-body">
                <div className="sched-oc-cause-head">{cause.size} jobs are failing for the same reason</div>
                <div className="sched-oc-cause-note">{(summary?.recent_failures || []).find((f) => f.error)?.error}</div>
              </div>
              <button type="button" className="sched-oc-cause-btn" disabled={bulkBusy} onClick={handleRetryCause}>
                Retry all {cause.size}
              </button>
              <button type="button" className="sched-oc-cause-btn ghost" onClick={() => { setFilter('fail'); setClosed({}); }}>
                Review the {cause.size} jobs
              </button>
            </div>
          )}

          {/* ── Filter chips + search ── */}
          <div className="sched-oc-filters">
            <div className="oc-chips">
              {FILTERS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  className={`oc-chip sched-oc-filterchip${filter === f.id ? ' on' : ''}`}
                  onClick={() => setFilter(f.id)}
                >
                  <span>{f.label}</span>
                  <span className="sched-oc-filterchip-count">{chipCounts[f.id]}</span>
                </button>
              ))}
            </div>
            <div className="oc-field sched-oc-search">
              <label htmlFor="sch-q">Find a job</label>
              <input
                id="sch-q"
                className="oc-input"
                type="text"
                placeholder="Job or stream name"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          </div>

          {/* ── Streams ── */}
          {!allCount ? (
            <div className="oc-blueprint oc-empty">
              <i className="oc-corner tl" aria-hidden="true" />
              <i className="oc-corner tr" aria-hidden="true" />
              <i className="oc-corner bl" aria-hidden="true" />
              <i className="oc-corner br" aria-hidden="true" />
              <p className="oc-empty-title">No schedules yet</p>
              <p className="oc-empty-sub">
                Create your first schedule to keep customers, parts, pricing and ship-to addresses
                flowing from Epicor into the middleware automatically.
              </p>
              <button type="button" className="oc-btn oc-btn-primary" onClick={() => navigate('/jobs/schedules/new')}>
                Create schedule
              </button>
            </div>
          ) : !visibleStreams.length ? (
            <div className="oc-blueprint oc-empty">
              <i className="oc-corner tl" aria-hidden="true" />
              <i className="oc-corner tr" aria-hidden="true" />
              <i className="oc-corner bl" aria-hidden="true" />
              <i className="oc-corner br" aria-hidden="true" />
              <p className="oc-empty-title">No job matches</p>
              <p className="oc-empty-sub">Clear the filter to see all {allCount} schedules.</p>
              <button
                type="button"
                className="oc-btn oc-btn-secondary"
                onClick={() => { setFilter('all'); setQuery(''); }}
              >
                Clear filter
              </button>
            </div>
          ) : (
            <div className="sched-oc-streams">
              {visibleStreams.map(({ stream, items, all }) => (
                <StreamCard
                  key={stream.id}
                  stream={stream}
                  items={items}
                  total={all.length}
                  open={!closed[stream.id] || anyFiltering}
                  onToggleOpen={() => setClosed((c) => ({ ...c, [stream.id]: !c[stream.id] }))}
                  failingIds={failingIds}
                  expandedId={expandedId}
                  onExpand={(id) => setExpandedId((cur) => (cur === id ? null : id))}
                  runsCache={runsCache}
                  onRunsLoaded={(id, runs) => setRunsCache((c) => (c[id] ? c : { ...c, [id]: runs }))}
                  onToggle={handleToggle}
                  onRunNow={handleRunNow}
                  onSaveCadence={handleSaveCadence}
                  onEdit={(s) => navigate(`/jobs/schedules/${s.id}/edit`)}
                  onHistory={(s) => navigate(`/jobs/schedules/${s.id}`)}
                  onDelete={(s) => setPendingDelete(s)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {pendingDelete && (
        <ConfirmModal
          title={`Delete "${pendingDelete.name}"?`}
          body="The schedule and its run history will be removed. Jobs already completed are unaffected. This cannot be undone."
          confirmLabel="Delete schedule"
          danger
          busy={deleteBusy}
          onConfirm={handleDelete}
          onCancel={() => setPendingDelete(null)}
        />
      )}
      <Toast message={toast?.message} error={toast?.error} />
    </div>
  );
}

// ── Stream card ─────────────────────────────────────────────────────────

function StreamCard({
  stream, items, total, open, onToggleOpen,
  failingIds, expandedId, onExpand, runsCache, onRunsLoaded,
  onToggle, onRunNow, onSaveCadence, onEdit, onHistory, onDelete,
}) {
  const push = stream.kind === 'push';
  const fails = items.filter((s) => failingIds.has(s.id)).length;
  const active = items.filter((s) => s.enabled).length;
  const health = fails > 0 ? `${fails} ${fails === 1 ? 'job' : 'jobs'} failing`
    : active === 0 ? 'All paused' : 'Healthy';
  const healthCls = fails > 0 ? 'bad' : active === 0 ? 'dim' : 'ok';

  return (
    <div className={`oc-blueprint sched-oc-stream${open ? '' : ' sched-oc-stream-closed'}`}>
      <i className="oc-corner tl" aria-hidden="true" />
      <i className="oc-corner tr" aria-hidden="true" />
      <i className="oc-corner bl" aria-hidden="true" />
      <i className="oc-corner br" aria-hidden="true" />

      <div className={`sched-oc-stream-head${push ? ' push' : ''}`}>
        <button
          type="button"
          className="sched-oc-stream-toggle"
          onClick={onToggleOpen}
          title={open ? 'Collapse this stream' : `Show the ${stream.label.toLowerCase()} jobs`}
          aria-expanded={open}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d={open ? 'M6 9l6 6 6-6' : 'M9 18l6-6-6-6'} />
          </svg>
        </button>
        <div className="sched-oc-stream-main">
          <div className="sched-oc-stream-titlerow">
            <span className="sched-oc-stream-title">{stream.label}</span>
            {stream.from && (
              <span className={`sched-oc-stream-dir${push ? ' push' : ''}`}>
                <span>{stream.from}</span>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
                <span>{stream.to}</span>
              </span>
            )}
          </div>
          <div className="sched-oc-stream-note">{stream.note}</div>
        </div>
        <div className="sched-oc-stream-health">
          <div className={`sched-oc-stream-health-state ${healthCls}`}>{health}</div>
          <div className="sched-oc-stream-health-note">{active} of {total} running on a schedule</div>
        </div>
      </div>

      {open && (
        <div>
          <div className="sched-oc-cols">
            <span>On</span>
            <span>Job &amp; cadence</span>
            <span>Last run</span>
            <span className="right">Next run</span>
          </div>
          {items.map((s) => (
            <JobRow
              key={s.id}
              schedule={s}
              failing={failingIds.has(s.id)}
              expanded={expandedId === s.id}
              onExpand={() => onExpand(s.id)}
              cachedRuns={runsCache[s.id]}
              onRunsLoaded={onRunsLoaded}
              onToggle={onToggle}
              onRunNow={onRunNow}
              onSaveCadence={onSaveCadence}
              onEdit={onEdit}
              onHistory={onHistory}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Job row ─────────────────────────────────────────────────────────────

function JobRow({
  schedule: s, failing, expanded, onExpand,
  cachedRuns, onRunsLoaded,
  onToggle, onRunNow, onSaveCadence, onEdit, onHistory, onDelete,
}) {
  const running = OPEN_STATUSES.has(s.last_status);
  const openRun = (s.recent_runs || []).some((r) => OPEN_STATUSES.has(r.status));
  const queuedOrRunning = running || openRun;
  const lastFailed = s.last_status === 'failed';
  const neverRun = !s.last_run_at;
  const held = !s.enabled && neverRun;
  const nextSoon = s.enabled && !queuedOrRunning && s.next_run_at
    && (new Date(s.next_run_at).getTime() - Date.now()) < 15 * 60000;
  const push = isPushJobType(s.job_type);

  const histRuns = (s.recent_runs || []).slice(-10);

  return (
    <div className={`sched-oc-job${failing ? ' failing' : ''}${expanded ? ' expanded' : ''}`}>
      <div className="sched-oc-jobrow">
        {/* On/off */}
        <button
          type="button"
          role="switch"
          aria-checked={s.enabled}
          aria-label={`Enable ${s.name}`}
          title={s.enabled
            ? `Pause ${s.name} — it stops running until you turn it back on`
            : `Turn on — ${s.name} will run ${cadenceText(s).toLowerCase()}`}
          className={`sched-oc-switch${s.enabled ? '' : ' off'}`}
          onClick={() => onToggle(s, !s.enabled)}
        >
          <span className="sched-oc-switch-knob" />
        </button>

        {/* Job & cadence */}
        <div className="sched-oc-jobcell">
          <div className={`sched-oc-jobname${s.enabled ? '' : ' paused'}`}>
            <Link to={`/jobs/schedules/${s.id}`}>{s.name}</Link>
          </div>
          <div className="sched-oc-jobcad">{cadenceText(s)}</div>
          <div className="sched-oc-tags">
            {!isAlwaysFull(s.job_type) && (
              <span
                className="sched-oc-tag help"
                title={s.sync_mode === 'incremental'
                  ? 'Incremental — reads only records changed since the last successful run.'
                  : 'Full refresh — reads every record, slower and heavier on Epicor.'}
              >
                {s.sync_mode === 'incremental' ? 'Incremental' : 'Full'}
              </span>
            )}
            {push
              ? (s.targets || []).map((t) => (
                <span key={targetKey(t)} className={`sched-oc-tag sched-oc-tag-co${t.environment === 'staging' ? ' staging' : ''}`}>
                  {targetChipLabel(t)}
                </span>
              ))
              : (s.companies || []).map((c) => (
                <span key={c} className="sched-oc-tag sched-oc-tag-co">{c}</span>
              ))}
          </div>
        </div>

        {/* Last run */}
        <div className="sched-oc-jobcell sched-oc-lastcell">
          <div className="sched-oc-lastrow">
            <span className={`sched-oc-lastdot ${running ? 'running' : lastFailed ? 'bad' : s.last_status ? 'ok' : ''}`} />
            <span className={`sched-oc-laststate ${running ? 'running' : lastFailed ? 'bad' : s.last_status ? 'ok' : 'none'}`}>
              {running ? 'Running' : s.last_status ? (STATUS_LABELS[s.last_status] || s.last_status) : 'Never run'}
            </span>
            {s.last_run_at && <span className="sched-oc-lastago" title={absoluteTime(s.last_run_at)}>{relativeTime(s.last_run_at)}</span>}
          </div>
          <LastRunMsg schedule={s} failing={failing && !running} held={held} />
          {!!histRuns.length && (
            <div className="sched-oc-hist" title="The last ten runs, oldest on the left.">
              {histRuns.map((r) => (
                <span
                  key={r.id}
                  className={`sched-oc-hist-bar${r.status === 'failed' ? ' bad' : OPEN_STATUSES.has(r.status) ? ' open' : ''}`}
                  style={{ height: r.status === 'failed' ? 14 : 8 }}
                  title={`${STATUS_LABELS[r.status] || r.status} — ${absoluteTime(r.enqueued_at)}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Next run + actions */}
        <div className="sched-oc-nextcell">
          {s.enabled ? (
            queuedOrRunning
              ? <span className="sched-oc-next soon">Running</span>
              : s.next_run_at
                ? <span className={`sched-oc-next${nextSoon ? ' soon' : ''}`} title={absoluteTime(s.next_run_at)}>{relativeTime(s.next_run_at)}</span>
                : <span className="sched-oc-next paused">Awaiting scheduler</span>
          ) : (
            <span className="sched-oc-next paused">Paused</span>
          )}
          <div className="sched-oc-actions">
            <button
              type="button"
              className="sched-oc-runbtn"
              onClick={() => onRunNow(s)}
              disabled={queuedOrRunning}
              title={queuedOrRunning
                ? 'A run is already queued or in progress'
                : push
                  ? 'Runs immediately and writes to the live storefronts'
                  : 'Runs immediately — staged in the middleware only'}
            >
              {lastFailed ? 'Retry' : 'Run now'}
            </button>
            <span className="sched-oc-menu">
              <OverflowMenu
                items={[
                  { label: 'Edit', onClick: () => onEdit(s) },
                  { label: 'View history', onClick: () => onHistory(s) },
                  { label: 'Delete', onClick: () => onDelete(s), danger: true },
                ]}
              />
            </span>
            <button
              type="button"
              className="sched-oc-detailbtn"
              onClick={onExpand}
              title="Run history and cadence"
              aria-expanded={expanded}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d={expanded ? 'M18 15l-6-6-6 6' : 'M6 9l6 6 6-6'} />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {held && (
        <div className="sched-oc-held">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20M12 8v4M12 16h.01" /></svg>
          <span>Paused — this schedule has never run.</span>
        </div>
      )}

      {expanded && (
        <JobDetail
          schedule={s}
          cachedRuns={cachedRuns}
          onRunsLoaded={onRunsLoaded}
          onSaveCadence={onSaveCadence}
          onClose={onExpand}
          onHistory={onHistory}
        />
      )}
    </div>
  );
}

function LastRunMsg({ schedule: s, failing, held }) {
  const split = lastRunAddedUpdated(s.job_type, s.last_job_counters);
  if (s.last_job_error && (failing || s.last_status === 'failed')) {
    return <div className="sched-oc-lastmsg bad" title={s.last_job_error}>{s.last_job_error}</div>;
  }
  if (split) {
    const title = s.job_type === 'inventory'
      ? 'Last run: new stock rows added · stock values actually changed'
      : 'Last run: new rows added · existing rows refreshed (values may be unchanged)';
    return (
      <div className="sched-oc-lastcounts" title={title}>
        +{split.added.toLocaleString()} new · {split.updated.toLocaleString()} updated
      </div>
    );
  }
  if (!s.last_run_at && !held) {
    return <div className="sched-oc-lastmsg">No history yet</div>;
  }
  return null;
}

// ── Expanded detail: recent runs + inline cadence editor ────────────────

function JobDetail({ schedule: s, cachedRuns, onRunsLoaded, onSaveCadence, onClose, onHistory }) {
  const [runs, setRuns] = useState(cachedRuns || null);
  const [runsError, setRunsError] = useState(null);
  const [cad, setCad] = useState(() => cadenceParts(s));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (cachedRuns) return;
    let cancelled = false;
    listScheduleRuns(s.id, { limit: 5 })
      .then((res) => {
        if (cancelled) return;
        const list = res?.runs || [];
        setRuns(list);
        onRunsLoaded(s.id, list);
      })
      .catch((e) => { if (!cancelled) setRunsError(errText(e, 'Could not load runs')); });
    return () => { cancelled = true; };
  }, [s.id, cachedRuns, onRunsLoaded]);

  const cadValLabel = cad.kind === 'daily' ? 'Time' : cad.kind === 'hours' ? 'Hours' : 'Minutes';
  const cadNote = cad.kind === 'daily'
    ? 'Overnight windows are cheapest on Epicor. Keep full refreshes between 02:00 and 04:00 Sydney.'
    : 'Incremental pulls under 15 minutes apart can overlap on a slow Epicor response.';

  const save = async () => {
    setSaving(true);
    try {
      await onSaveCadence(s, cad);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sched-oc-detail">
      <div>
        <div className="sched-oc-detail-title">Recent runs</div>
        {runsError && <div className="sched-oc-detailerror">{runsError}</div>}
        {!runs && !runsError && <div className="sched-oc-detailloading">Loading…</div>}
        {runs && !runs.length && (
          <div className="sched-oc-detailloading">No runs yet — the scheduler fires on its cadence, or use Run now.</div>
        )}
        {!!(runs && runs.length) && (
          <div className="sched-oc-runs">
            {runs.map((r) => (
              <div className="sched-oc-runrow" key={r.id}>
                <span className="at" title={absoluteTime(r.enqueued_at)}>{relativeTime(r.enqueued_at)}</span>
                <span className="dur">{durationText(r.job_started_at, r.job_finished_at)}</span>
                <span className={`msg ${r.status === 'failed' ? 'bad' : 'ok'}`}>
                  {r.status === 'failed' ? (r.error || 'Failed') : (STATUS_LABELS[r.status] || r.status)}
                </span>
              </div>
            ))}
          </div>
        )}
        <div className="sched-oc-historylink">
          <button type="button" className="oc-btn oc-btn-ghost" style={{ padding: '4px 10px', fontSize: 12.5 }} onClick={() => onHistory(s)}>
            Open full history
          </button>
        </div>
      </div>

      <div>
        <div className="sched-oc-detail-title">Cadence</div>
        {cad.multi ? (
          <>
            <p className="sched-oc-detailnote">
              Runs several times a day ({(s.run_times || []).join(', ')}). Editing multiple daily
              times needs the full editor.
            </p>
            <div className="sched-oc-detailbtns">
              <button type="button" className="oc-btn oc-btn-secondary" onClick={onClose}>Close</button>
              <Link className="oc-btn oc-btn-primary" to={`/jobs/schedules/${s.id}/edit`}>Open full editor</Link>
            </div>
          </>
        ) : (
          <>
            <div className="sched-oc-cadform">
              <div className="oc-field">
                <label htmlFor={`cad-kind-${s.id}`}>Runs</label>
                <select
                  id={`cad-kind-${s.id}`}
                  className="oc-input"
                  value={cad.kind}
                  onChange={(e) => {
                    const kind = e.target.value;
                    setCad({ kind, value: kind === 'daily' ? '02:00' : kind === 'hours' ? '1' : '30' });
                  }}
                >
                  <option value="mins">Every N minutes</option>
                  <option value="hours">Every N hours</option>
                  <option value="daily">Daily at a time</option>
                </select>
              </div>
              <div className="oc-field value">
                <label htmlFor={`cad-val-${s.id}`}>{cadValLabel}</label>
                <input
                  id={`cad-val-${s.id}`}
                  className="oc-input"
                  type="text"
                  value={cad.value || ''}
                  onChange={(e) => setCad((c) => ({ ...c, value: e.target.value }))}
                />
              </div>
            </div>
            <p className="sched-oc-detailnote">{cadNote}</p>
            <div className="sched-oc-detailbtns">
              <button type="button" className="oc-btn oc-btn-secondary" onClick={onClose}>Close</button>
              <button type="button" className="oc-btn oc-btn-primary" disabled={saving} onClick={save}>
                {saving ? 'Saving…' : 'Save cadence'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
